import { Fragment, memo, useCallback, useMemo, type ReactNode } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type {
  FormFieldData,
  FormSchemaVersionDetailData,
  FormSectionData,
} from '@/services/api-types';
import { evaluateVisibility } from '@/components/forms/visibility-evaluator';
import { evaluateFormula } from '@/components/forms/formula-evaluator';
import { TextField } from './fields/TextField';
import { TextAreaField } from './fields/TextAreaField';
import { DateField } from './fields/DateField';
import { PhoneField } from './fields/PhoneField';
import { EmailField } from './fields/EmailField';
import { NumberField } from './fields/NumberField';
import { RadioField } from './fields/RadioField';
import { SelectField } from './fields/SelectField';
import { CheckboxGroupField } from './fields/CheckboxGroupField';
import { TableField, type TableRow } from './fields/TableField';
import { ComputedField } from './fields/ComputedField';

export interface DynamicFormRendererProps {
  schemaVersion: FormSchemaVersionDetailData;
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly?: boolean;
  /**
   * Per-field validation messages. Keyed by field key. When set, the matching
   * field renders a red outline and an inline message — used by the WYSIWYG
   * editor for live structural validation and by FormTabPanel to surface
   * 422 responses on the offending field.
   */
  errors?: Record<string, string>;
  /**
   * Optional wrapper for each field's rendered output. The editor uses this
   * to add selection chrome (handle, hover outline, click-to-select) around
   * each field without re-implementing the renderer's layout logic.
   */
  renderFieldWrapper?: (args: { field: FormFieldData; defaultNode: ReactNode }) => ReactNode;
  /**
   * Optional wrapper for each section's rendered output. The editor uses this
   * to add section selection chrome (insert-slot above/below, click-to-select).
   */
  renderSectionWrapper?: (args: { section: FormSectionData; defaultNode: ReactNode }) => ReactNode;
  /**
   * When true, all fields render regardless of `showIf`. Lets the WYSIWYG
   * editor switch between live mode (default) and a structural view that
   * surfaces every field for editing, even ones currently hidden by their
   * conditional visibility.
   */
  forceShowAll?: boolean;
}

/**
 * Renders a dynamic form from a FormSchemaVersion + current values map.
 *
 * - Sections render in order with titles.
 * - Fields whose visibility resolves to `false` are NOT rendered (removed from
 *   the DOM, not CSS-hidden).
 * - `onChange(next)` fires with a new object (spread copy — never mutates).
 * - Computed fields are passed the whole values map so they can self-evaluate.
 *
 * See `docs/house-check-request/implementation-plan.md` §3.1.
 */
export const DynamicFormRenderer = memo(function DynamicFormRenderer({
  schemaVersion,
  values,
  onChange,
  readOnly = false,
  errors,
  renderFieldWrapper,
  renderSectionWrapper,
  forceShowAll = false,
}: DynamicFormRendererProps) {
  const { theme } = useTheme();

  const visibility = useMemo(
    // forceShowAll: empty map → every field's lookup is `undefined !== false` → visible.
    () => (forceShowAll ? {} : evaluateVisibility(schemaVersion.sections, values)),
    [schemaVersion.sections, values, forceShowAll],
  );

  // Resolve computed-field values at the renderer level so chained formulas
  // (e.g. remittanceTotal = subtotal + salesTax, where each is computed)
  // work end-to-end. Mirrors the backend (FormInstanceService.evaluateComputedInDocumentOrder):
  // walk fields in document order, and as each computed resolves, add it to
  // the numeric scope so later computeds in the same pass see it.
  //
  // Why not the simpler "build scope once per pass, iterate until stable"
  // approach: evaluateFormula treats missing identifiers as 0 (safe-degradation
  // contract), so the first time a dependent computed evaluates with a missing
  // dependency it stores `dependent_field = 0` and gets skipped on later
  // passes. Document-order accumulation avoids that trap entirely.
  const enrichedValues = useMemo(() => {
    const out: Record<string, unknown> = { ...values };
    const numericScope: Record<string, number> = {};

    // Seed scope with non-computed numeric values from the input. Stored
    // computed values from a previous save are intentionally NOT seeded —
    // we'll recompute them in document order so live edits to inputs flow
    // through to dependents without waiting for a save.
    for (const section of schemaVersion.sections) {
      for (const field of section.fields ?? []) {
        if (field.type === 'computed') continue;
        const v = out[field.key];
        if (v == null || v === '') continue;
        const n = Number(v as number | string);
        if (!Number.isNaN(n)) numericScope[field.key] = n;
      }
    }

    for (const section of schemaVersion.sections) {
      for (const field of section.fields ?? []) {
        if (field.type !== 'computed' || typeof field.formula !== 'string' || !field.formula) continue;
        const r = evaluateFormula(field.formula, numericScope);
        if (r != null && !Number.isNaN(r)) {
          out[field.key] = r;
          numericScope[field.key] = r;
        } else {
          delete out[field.key];
        }
      }
    }

    return out;
  }, [values, schemaVersion.sections]);

  const setField = useCallback(
    (key: string, next: unknown) => {
      const nextValues = { ...values, [key]: next };
      onChange(nextValues);
    },
    [values, onChange],
  );

  // Partition sections into main + sidebar so the renderer can lay them out in
  // two columns on wider screens. Sidebar sections retain their internal field
  // grid (smaller column, but the section's own layout is unchanged).
  const mainSections = schemaVersion.sections.filter((s) => !s.sidebar);
  const sidebarSections = schemaVersion.sections.filter((s) => !!s.sidebar);

  const renderField = (field: FormFieldData): ReactNode => {
    const node = (
      <FieldByType
        field={field}
        value={enrichedValues[field.key]}
        values={enrichedValues}
        onChange={(next) => setField(field.key, next)}
        readOnly={readOnly}
        error={errors?.[field.key]}
      />
    );
    return renderFieldWrapper ? renderFieldWrapper({ field, defaultNode: node }) : node;
  };

  const renderSection = (section: FormSectionData): ReactNode => {
    // Build render groups: each group is a list of consecutive fields that
    // share a row (the head of the group is a normal field; subsequent
    // fields with `inlineWithPrevious: true` join its row). A field with
    // `subheading` always starts a new group, even if it's marked inline.
    const visibleFields = section.fields.filter((f) => visibility[f.key] !== false);
    const groups: { head: FormFieldData; tail: FormFieldData[] }[] = [];
    for (const field of visibleFields) {
      const startNew = !field.inlineWithPrevious || groups.length === 0 || !!field.subheading;
      if (startNew) {
        groups.push({ head: field, tail: [] });
      } else {
        groups[groups.length - 1].tail.push(field);
      }
    }

    const sectionNode = (
      // NOTE: must be <div>, not <section>. Legacy `Content/app.css` has a
      // global `section { display: none; }` rule used as a conditional-show
      // mechanism by the legacy SPA.
      <div className="dynamic-form-section">
        {section.title ? (
          <div className="dynamic-form-section-header">{section.title}</div>
        ) : null}
        <div className="dynamic-form-section-body">
          <div className="dynamic-form-section-fields">
          {groups.map((group) => {
            // Wide groups span both columns of the section grid: textarea/table,
            // checkboxGroup, any group with an inline tail, or an explicit
            // `wide: true` flag. Subheading dividers render as their own
            // wide row above the field, so the field itself stays in its
            // normal column.
            const hasSubheading = group.head.subheading !== undefined;
            const isWide =
              group.head.type === 'textarea' ||
              group.head.type === 'table' ||
              group.head.type === 'checkboxGroup' ||
              group.tail.length > 0 ||
              !!group.head.wide;
            return (
              <Fragment key={group.head.key}>
                {hasSubheading ? (
                  <div className="dynamic-form-row-wrapper dynamic-form-row-wrapper--wide">
                    <div className="dynamic-form-subheading">
                      {group.head.subheading ? <h4>{group.head.subheading}</h4> : null}
                    </div>
                  </div>
                ) : null}
                <div
                  className={
                    'dynamic-form-row-wrapper' +
                    (isWide ? ' dynamic-form-row-wrapper--wide' : '')
                  }
                >
                  <div
                    className={
                      (group.tail.length > 0
                        ? 'dynamic-form-inline-row'
                        : 'dynamic-form-row') +
                      (group.head.layout === 'horizontal'
                        ? ' dynamic-form-row--horizontal'
                        : '')
                    }
                  >
                    {renderField(group.head)}
                    {group.tail.map((f) => (
                      <Fragment key={f.key}>{renderField(f)}</Fragment>
                    ))}
                  </div>
                </div>
              </Fragment>
            );
          })}
          </div>
        </div>
      </div>
    );

    return renderSectionWrapper ? renderSectionWrapper({ section, defaultNode: sectionNode }) : sectionNode;
  };

  return (
    <div className={'dynamic-form-renderer' + (theme ? ' ' + theme : '')}>
      {sidebarSections.length > 0 ? (
        <div className="dynamic-form-layout">
          <div className="dynamic-form-main">
            {mainSections.map((s) => <Fragment key={s.id}>{renderSection(s)}</Fragment>)}
          </div>
          <div className="dynamic-form-sidebar">
            {sidebarSections.map((s) => <Fragment key={s.id}>{renderSection(s)}</Fragment>)}
          </div>
        </div>
      ) : (
        // No sidebar sections — render flat (existing behavior).
        mainSections.map((s) => <Fragment key={s.id}>{renderSection(s)}</Fragment>)
      )}
    </div>
  );
});

interface FieldByTypeProps {
  field: FormFieldData;
  value: unknown;
  values: Record<string, unknown>;
  onChange: (next: unknown) => void;
  readOnly?: boolean;
  error?: string;
}

function FieldByType({ field, value, values, onChange, readOnly, error }: FieldByTypeProps) {
  switch (field.type) {
    case 'text':
      return (
        <TextField
          field={field}
          value={(value as string | null | undefined) ?? ''}
          onChange={onChange as (v: string | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'textarea':
      return (
        <TextAreaField
          field={field}
          value={(value as string | null | undefined) ?? ''}
          onChange={onChange as (v: string | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'date':
      return (
        <DateField
          field={field}
          value={(value as string | null | undefined) ?? ''}
          onChange={onChange as (v: string | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'phone':
      return (
        <PhoneField
          field={field}
          value={(value as string | null | undefined) ?? ''}
          onChange={onChange as (v: string | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'email':
      return (
        <EmailField
          field={field}
          value={(value as string | null | undefined) ?? ''}
          onChange={onChange as (v: string | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'number':
      return (
        <NumberField
          field={field}
          value={(value as number | null | undefined) ?? null}
          onChange={onChange as (v: number | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'radio':
      return (
        <RadioField
          field={field}
          value={(value as string | null | undefined) ?? ''}
          onChange={onChange as (v: string | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'select':
      return (
        <SelectField
          field={field}
          value={(value as string | null | undefined) ?? ''}
          onChange={onChange as (v: string | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'checkboxGroup':
      return (
        <CheckboxGroupField
          field={field}
          value={(value as string[] | null | undefined) ?? []}
          onChange={onChange as (v: string[] | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'table':
      return (
        <TableField
          field={field}
          value={(value as TableRow[] | null | undefined) ?? []}
          onChange={onChange as (v: TableRow[] | null | undefined) => void}
          readOnly={readOnly}
          error={error}
        />
      );
    case 'computed':
      return (
        <ComputedField
          field={field}
          value={(value as number | null | undefined) ?? null}
          values={values}
          onChange={onChange as (v: number | null | undefined) => void}
          readOnly={readOnly}
        />
      );
    default:
      return null;
  }
}
